import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImagePicker from "@/components/ImagePicker";

export default function AdminGuides() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [guides, setGuides] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") { nav("/"); return; }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const load = async () => {
        try {
            const { data } = await api.get("/guide?only_published=false");
            setGuides(data);
        } catch { setGuides([]); }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!editing) return;
        setBusy(true);
        try {
            await api.put(`/guide/${editing.slug}`, {
                title: editing.title,
                subtitle: editing.subtitle,
                hero_image: editing.hero_image,
                intro: editing.intro,
                body_html: editing.body_html,
                related_trails: editing.related_trails || [],
                season: editing.season || null,
                seo_title: editing.seo_title || null,
                seo_description: editing.seo_description || null,
                published: !!editing.published,
            });
            toast.success("Guida salvata");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally { setBusy(false); }
    };

    if (!user) return <div className="max-w-3xl mx-auto px-5 py-20 text-zinc-400">Caricamento…</div>;

    if (editing) return (
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-12">
            <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-abnormal-red inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Tutte le guide
            </button>
            <h1 className="font-display text-4xl uppercase mt-4">{editing.title}</h1>
            <p className="text-xs text-zinc-500 tracking-[0.2em] uppercase mt-1">/guide/{editing.slug}</p>

            <form onSubmit={save} className="mt-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                    <F label="Titolo">
                        <input data-testid="guide-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm" />
                    </F>
                    <F label="Sottotitolo">
                        <input data-testid="guide-subtitle" value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm" />
                    </F>
                </div>
                <F label="Immagine hero">
                    <ImagePicker value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v })} testidPrefix="guide-hero" />
                </F>
                <F label="Intro (paragrafo iniziale, max 1000)">
                    <textarea data-testid="guide-intro" rows={3} maxLength={1000} value={editing.intro || ""} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm min-h-[90px]" />
                </F>
                <F label="Corpo HTML (usa <p>, <h2>, <ul><li>, <strong>, <a href='...'>)" hint="Suggerimento: scrivi i paragrafi in <p>, i titoli sezione in <h2>, le liste in <ul><li>.">
                    <textarea data-testid="guide-body" rows={18} value={editing.body_html || ""} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm font-mono min-h-[400px]" />
                </F>
                <div className="grid md:grid-cols-3 gap-4">
                    <F label="Stagione (opz.)" hint="primavera, estate, autunno, inverno">
                        <input data-testid="guide-season" value={editing.season || ""} onChange={(e) => setEditing({ ...editing, season: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm" />
                    </F>
                    <F label="Trail collegati (slug separati da virgola)" hint="es: sassicaia, labbrata">
                        <input
                            data-testid="guide-trails"
                            value={(editing.related_trails || []).join(", ")}
                            onChange={(e) => setEditing({ ...editing, related_trails: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                            className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm"
                        />
                    </F>
                    <F label="Pubblicata">
                        <button type="button" onClick={() => setEditing({ ...editing, published: !editing.published })} className={`w-full px-3 py-2 text-sm uppercase tracking-wider font-bold border transition-colors ${editing.published ? "border-abnormal-red text-abnormal-red" : "border-zinc-700 text-zinc-500"}`}>
                            {editing.published ? <span className="inline-flex items-center gap-2"><Eye size={14} /> Pubblicata</span> : <span className="inline-flex items-center gap-2"><EyeOff size={14} /> Bozza</span>}
                        </button>
                    </F>
                </div>
                <div className="grid md:grid-cols-2 gap-4 border-t border-white/10 pt-5">
                    <F label="SEO Title (max 60 char)">
                        <input data-testid="guide-seo-title" maxLength={70} value={editing.seo_title || ""} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm" />
                    </F>
                    <F label="SEO Description (max 160 char)">
                        <textarea data-testid="guide-seo-desc" maxLength={170} rows={2} value={editing.seo_description || ""} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm" />
                    </F>
                </div>
                <button data-testid="guide-save" type="submit" disabled={busy}
                    className="bg-abnormal-red text-white uppercase tracking-wider text-sm font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-white hover:text-abnormal-red transition-colors disabled:opacity-50">
                    <Save size={16} /> {busy ? "Salvataggio…" : "Salva guida"}
                </button>
            </form>
        </div>
    );

    return (
        <div data-testid="admin-guides-page" className="max-w-[1100px] mx-auto px-5 md:px-10 py-14">
            <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-abnormal-red inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Admin
            </Link>
            <div className="mt-6 flex items-center gap-3">
                <BookOpen className="text-abnormal-red" />
                <h1 className="font-display text-5xl md:text-7xl uppercase leading-none">Guide</h1>
            </div>
            <p className="text-zinc-400 mt-4 max-w-2xl">Scrivi e pubblica le guide SEO della sezione <span className="text-abnormal-red">/guide</span>. Ogni guida ha un body HTML, immagine hero, trail collegati e meta tag.</p>

            <div className="mt-10 border border-white/10 divide-y divide-white/10">
                {guides.map((g) => (
                    <div key={g.slug} className="px-5 py-4 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                {g.published ? <span className="text-[10px] text-abnormal-red border border-abnormal-red/50 px-2 py-0.5 uppercase tracking-wider font-bold">Pubblicata</span> : <span className="text-[10px] text-zinc-500 border border-zinc-700 px-2 py-0.5 uppercase tracking-wider font-bold">Bozza</span>}
                                {g.season && <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{g.season}</span>}
                            </div>
                            <p className="font-display text-2xl uppercase mt-1 truncate">{g.title}</p>
                            <p className="text-xs text-zinc-500">/guide/{g.slug}</p>
                        </div>
                        <button
                            data-testid={`guide-edit-${g.slug}`}
                            onClick={() => setEditing({ ...g })}
                            className="border border-white/20 hover:border-abnormal-red hover:text-abnormal-red text-xs uppercase tracking-[0.25em] font-bold px-4 py-2 transition-colors"
                        >
                            Modifica
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function F({ label, hint, children }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1 block">{label}</label>
            {children}
            {hint && <p className="text-[11px] text-zinc-600 mt-1">{hint}</p>}
        </div>
    );
}
